import { z } from 'zod';
import { JsonObjectSchema, PaginationQuerySchema, createPaginatedResponseSchema } from './common.js';
import { ConversationChannelSchema } from './chat-channel.js';
import { AdminButtonSetSchema, ButtonPressOutcomeSchema, ChatSuggestionTapSchema, RenderedButtonsSchema } from './chat-buttons.js';
// =============================================================================
// Chat
// =============================================================================
export const ChatMessageSchema = z.object({
    role: z.enum(['user', 'assistant']),
    content: z.string(),
});
export const ChatRequestSchema = z.object({
    messages: z.array(ChatMessageSchema).min(1),
    conversationId: z.string().optional(),
});
export const ChatResponseSchema = z.object({
    message: z.string(),
    conversationId: z.string().optional(),
});
// =============================================================================
// Chat entry point
// =============================================================================
/**
 * Where a conversation was started from. Recorded once, on `chat.conversation.created`;
 * a conversation whose event carries none predates the field or came from a surface that
 * does not report one, and reads as unknown.
 */
export const ChatEntryPointSchema = z.enum([
    'composer',
    'voice',
    'ask-bill-suggestion',
    'ask-bill-prompt',
    'omnibox',
    'chat-starter',
    'onboarding-welcome',
    'system-notification',
]);
/**
 * The entry points a client may claim on a conversation-creating request. `voice`,
 * `onboarding-welcome` and `system-notification` are stamped by the server from the
 * route or listener that creates the thread, so a client can never assert them.
 */
export const ChatStreamEntryPointSchema = ChatEntryPointSchema.extract([
    'composer',
    'ask-bill-suggestion',
    'ask-bill-prompt',
    'omnibox',
    'chat-starter',
]);
// AG-UI Streaming Chat
export const ChatStreamRequestSchema = z.object({
    runId: z.string(),
    messages: z.array(ChatMessageSchema).min(1),
    conversationId: z.string().optional(),
    /** Where the user started this conversation. Read only when the run creates the conversation. */
    entryPoint: ChatStreamEntryPointSchema.optional(),
    /** documentIds of CONVERSATION-parented attachments to associate with this message.
     *  Requires conversationId to be present; omit when there are no attachments. */
    attachments: z.array(z.string().uuid()).max(10).optional(),
});
/**
 * A tap on a suggestion button. The button lives in one conversation, so the tap names
 * it; the server sends the suggestion's own stored message, so the client never supplies
 * the text.
 */
export const ChatSuggestionStreamRequestSchema = z.strictObject({
    ...ChatSuggestionTapSchema.shape,
    runId: z.string(),
    conversationId: z.string(),
});
// Voice Note Streaming Chat
export const ChatVoiceStreamRequestSchema = z.object({
    runId: z.string(),
    audio: z.string(), // base64-encoded audio
    mimeType: z.enum(['audio/webm', 'audio/ogg', 'audio/webm;codecs=opus', 'audio/ogg;codecs=opus']),
    conversationId: z.string().optional(),
});
// Conversation schemas
export const ConversationSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    userId: z.string(),
    title: z.string().nullable(),
    /** First user message content, truncated - used as preview when title is null */
    preview: z.string().nullable().optional(),
    channel: ConversationChannelSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * Body of `POST /accounts/:accountId/conversations`. Kept a plain object (not strict):
 * the route is on the lenient request-schema allowlist, and tightening it is its own rollout.
 */
export const CreateConversationRequestSchema = z.object({
    idempotencyKey: z.string().min(1),
    /** Where the user started the conversation; the composer's attachment path sends `composer`. */
    entryPoint: ChatStreamEntryPointSchema.optional(),
});
/** User-facing labels for the channel a conversation came in on. */
export const CONVERSATION_CHANNEL_LABELS = {
    web: 'Web app',
    whatsapp: 'WhatsApp',
};
export const StoredMessageRoleSchema = z.enum(['human', 'ai', 'tool', 'system', 'thinking']);
export const ToolCallSchema = z.object({
    id: z.string().optional(),
    name: z.string(),
    args: JsonObjectSchema,
});
/**
 * Prefix stamped onto a voice note's persisted content so the LLM sees it
 * transcribed from speech (`[Voice note]: <transcript>`). The prefix is
 * internal vocabulary - a display surface must strip it via
 * `stripVoiceNoteContentPrefix` rather than rendering the raw content, and
 * must not duplicate the literal. Single source of truth for both the
 * backend chat-stream / WhatsApp routes that stamp it and any frontend
 * reader that needs to strip it back off a persisted message.
 */
export const VOICE_NOTE_CONTENT_PREFIX = '[Voice note]: ';
/**
 * Strips the voice-note prefix from a persisted message's content, if present.
 * A legacy row whose transcript itself happens to start with the same text
 * only has the leading occurrence removed, which is the correct behaviour -
 * the prefix is stamped once, at the start, never repeated.
 */
export function stripVoiceNoteContentPrefix(content) {
    return content.startsWith(VOICE_NOTE_CONTENT_PREFIX)
        ? content.slice(VOICE_NOTE_CONTENT_PREFIX.length)
        : content;
}
export const ConversationMessageSchema = z.object({
    id: z.string(),
    conversationId: z.string(),
    role: StoredMessageRoleSchema,
    content: z.string(),
    traceId: z.string().nullable(),
    toolCalls: z.array(ToolCallSchema).nullable(),
    toolCallId: z.string().nullable(),
    toolName: z.string().nullable(),
    /** Structured metadata stored alongside the message content.
     *  For user messages with attachments: { attachments: [{ documentId, filename, mimeType, sizeBytes }] }.
     *  For voice-note messages: { voiceNoteId: string }.
     *  Legacy rows (created before this feature) have null. */
    metadata: JsonObjectSchema.nullable(),
    createdAt: z.string(),
    /** Tappable buttons under an assistant reply, present only while its set is still on offer. */
    buttons: RenderedButtonsSchema.optional(),
});
/** One file a user attached to a message, as stored in a human row's `metadata.attachments`. */
export const MessageAttachmentSchema = z.object({
    documentId: z.string().uuid(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    /** PDFs only. True when the PDF has a readable text layer; absent when unknown (legacy row, non-PDF, describe failed). */
    readableText: z.boolean().optional(),
    /** PDFs only. Absent when unknown or the PDF would not open. */
    pageCount: z.number().int().min(1).optional(),
});
/** A button press: what it did, and the rows it appended to the conversation (oldest first). */
export const ButtonPressResponseSchema = z.object({
    outcome: ButtonPressOutcomeSchema,
    /** True when an earlier press already decided this set; nothing ran now and no rows were added. */
    alreadyDecided: z.boolean(),
    /** The fixed sentence the assistant answers the press with. */
    receipt: z.string(),
    messages: z.array(ConversationMessageSchema),
});
export const ConversationWithMessagesSchema = ConversationSchema.extend({
    messages: z.array(ConversationMessageSchema),
});
export const ConversationListResponseSchema = createPaginatedResponseSchema(ConversationSchema);
export const ConversationMessagesResponseSchema = createPaginatedResponseSchema(ConversationMessageSchema);
export const AccountIdParamsSchema = z.object({
    accountId: z.string(),
});
export const ConversationParamsSchema = z.object({
    accountId: z.string(),
    conversationId: z.string(),
});
// =============================================================================
// Super Admin Conversations
// =============================================================================
export const SuperAdminConversationMessageSchema = ConversationMessageSchema.extend({
    metadata: JsonObjectSchema.nullable(),
    voiceNoteId: z.string().nullable(),
    /** The buttons offered under this reply, when it carried any. Admin only. */
    buttonSet: AdminButtonSetSchema.nullable(),
});
// ── ChatTrace schemas ────────────────────────────────────────────────────
/**
 * Why an agent run terminated on the error path (`ChatTrace.failureReason`). Closed
 * union so `chat.completion.created` rows and admin run filters are groupable.
 * Mirrors `LlmTransportFailure` in `agents/llm-transport-error.ts` for the first two
 * cases, which is what actually detects them.
 */
export const ChatFailureReasonSchema = z.enum([
    'RECURSION_LIMIT',
    'RATE_LIMITED',
    'TIMED_OUT',
    'APP_ERROR',
    'UNEXPECTED_ERROR',
]);
export const ChatTraceSchema = z.object({
    id: z.string(),
    conversationId: z.string(),
    accountId: z.string(),
    userId: z.string(),
    systemPrompt: z.string(),
    /** The per-turn context block sent with the run's latest message; null on runs that
     *  predate storing it apart from `systemPrompt`, which then carries it inline. */
    turnContext: z.string().nullable(),
    modelName: z.string(),
    status: z.enum(['running', 'completed', 'error']),
    error: z.string().nullable(),
    promptTokens: z.number(),
    completionTokens: z.number(),
    /** Null on a run that predates span capture, never 0. */
    cachedTokens: z.number().nullable(),
    /** Prompt tokens written to the provider's prompt cache. Null on a run recorded before cache writes were captured. */
    cacheWriteTokens: z.number().nullable(),
    toolCallCount: z.number().nullable(),
    firstTokenAt: z.string().nullable(),
    /** Integer micro-USD (1 USD = 1,000,000), priced from the trace's own token
     *  counts at `startedAt`. Null when the model has no price-table entry. */
    costMicroUsd: z.number().nullable(),
    failureReason: ChatFailureReasonSchema.nullable(),
    /** False when the run predates span capture (`inputManifest IS NULL`) - the run
     *  detail and model-call-input endpoints have nothing to show for it. */
    hasSpanDetail: z.boolean(),
    /** The last message id of the run's input: the message the run answered. Null on runs
     *  that predate span capture (no input manifest). */
    turnMessageId: z.string().nullable(),
    startedAt: z.string(),
    completedAt: z.string().nullable(),
    metadata: JsonObjectSchema.nullable(),
});
export const ChatTraceDetailParamsSchema = z.object({
    conversationId: z.string(),
    traceId: z.string(),
});
// ── Super Admin Conversations ────────────────────────────────────────────
export const SuperAdminConversationSummarySchema = z.object({
    id: z.string(),
    accountId: z.string(),
    accountName: z.string(),
    userId: z.string(),
    userEmail: z.string(),
    userName: z.string(),
    title: z.string().nullable(),
    channel: ConversationChannelSchema,
    messageCount: z.number(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const SuperAdminConversationListQuerySchema = z.object({
    accountId: z.uuid('accountId must be a valid UUID').optional(),
    userId: z.uuid('userId must be a valid UUID').optional(),
    search: z.string().optional(),
}).extend(PaginationQuerySchema.shape);
export const SuperAdminConversationListResponseSchema = createPaginatedResponseSchema(SuperAdminConversationSummarySchema);
// Conversation metadata + traces + total message count, WITHOUT the messages
// themselves. Messages are loaded separately via the paginated messages
// endpoint so a long thread (hundreds of WhatsApp messages) is not fetched in
// one payload. `messageCount` is the full conversation size for the header
// stat; `traces` is small (one per AI completion) and drives the per-turn
// detail expanders, so it is loaded in full here.
export const SuperAdminConversationDetailSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    accountName: z.string(),
    userId: z.string(),
    userEmail: z.string(),
    userName: z.string(),
    title: z.string().nullable(),
    channel: ConversationChannelSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
    messageCount: z.number(),
    traces: z.array(ChatTraceSchema),
});
export const SuperAdminConversationMessagesResponseSchema = createPaginatedResponseSchema(SuperAdminConversationMessageSchema);
export const SuperAdminConversationDetailParamsSchema = z.object({
    conversationId: z.string(),
});
export const SuperAdminConversationAttachmentParamsSchema = SuperAdminConversationDetailParamsSchema.extend({
    documentId: z.string().uuid(),
});
// =============================================================================
// Conversation Documents
// =============================================================================
export const ConversationDocumentResponseSchema = z.object({
    id: z.string(),
    conversationId: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    createdAt: z.string(),
    downloadUrl: z.string(),
});
